import React, { useEffect, useMemo } from 'react';
import { Loader2, History, Users, Briefcase } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getRoleService } from '@/services/roleService';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { Role } from '@/types/role';
import { RateHistoryTab } from './RateHistoryTab';
import { formatCurrency, formatPercentage, formatDate } from '@/utils/formatters';
import { toast } from 'sonner';

interface RoleDetailDialogProps {
  roleId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RoleDetailDialog: React.FC<RoleDetailDialogProps> = ({
  roleId,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [role, setRole] = React.useState<Role | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const loadRole = async () => {
    setIsLoading(true);
    try {
      const data = await roleService.getRoleById(roleId);
      setRole(data);
    } catch (err) {
      console.error('Error loading role:', err);
      toast.error('Failed to load role details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadRole();
    }
  }, [roleId, open]);

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] bg-background">
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading role details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto bg-background">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="text-2xl">{role.name}</DialogTitle>
                <DialogDescription className="mt-2">
                  {role.description || 'No description provided'}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={role.status === 'Active' ? 'default' : 'secondary'}>
                  {role.status}
                </Badge>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Managed in Resource Manager
                </Badge>
              </div>
            </div>
          </DialogHeader>

          {/* Current Rate Summary */}
          {role.currentRate && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Current Cost Rate</div>
                <div className="text-2xl font-bold">
                  {role.currentRate.costRate
                    ? `£${role.currentRate.costRate.toFixed(2)}/hr`
                    : '—'}
                </div>
                {role.currentRate.costRate && (
                  <div className="text-xs text-muted-foreground mt-1">
                    £{(role.currentRate.costRate * 8).toFixed(2)}/day
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Current Charge Rate</div>
                <div className="text-2xl font-bold">
                  {role.currentRate.billingRate
                    ? `£${role.currentRate.billingRate.toFixed(2)}/hr`
                    : '—'}
                </div>
                {role.currentRate.billingRate && (
                  <div className="text-xs text-muted-foreground mt-1">
                    £{(role.currentRate.billingRate * 8).toFixed(2)}/day
                  </div>
                )}
                {role.currentRate.marginPercentage !== null && role.currentRate.marginPercentage !== undefined && (
                  <div className="text-xs mt-2">
                    <span className="text-muted-foreground">Margin: </span>
                    <span className={`font-medium ${role.currentRate.marginPercentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercentage(role.currentRate.marginPercentage)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Employees</div>
                <div className="text-lg font-semibold">{role.employeeCount}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <Briefcase className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Projects</div>
                <div className="text-lg font-semibold">{role.projectCount}</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="history" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-2" />
                Rate History
              </TabsTrigger>
              <TabsTrigger value="details">
                Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="history" className="mt-4">
              <RateHistoryTab roleId={roleId} onRateAdded={loadRole} />
            </TabsContent>

            <TabsContent value="details" className="mt-4 space-y-4">
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Created</div>
                  <div className="text-sm">
                    {formatDate(role.createdAt)}
                    {role.createdByName && ` by ${role.createdByName}`}
                  </div>
                </div>
                {role.archivedAt && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Archived</div>
                    <div className="text-sm">
                      {formatDate(role.archivedAt)}
                      {role.archivedByName && ` by ${role.archivedByName}`}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
    </Dialog>
  );
};
